import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";

const INITIAL_UI = {
  search: "",
  unitType: "",
  floor: "",
  ordering: "newest",
  priceMin: "",
  priceMax: "",
  showSold: false, // off = show unsold (default), on = show sold
};

const ORDERING_OPTIONS = [
  { value: "newest", label: "Newest First", api: "-created_at" },
  { value: "priceAsc", label: "Price: Low to High", api: "initial_price" },
  { value: "priceDesc", label: "Price: High to Low", api: "-initial_price" },
  { value: "nr", label: "Unit Number", api: "nr" },
];

const SEARCH_DEBOUNCE_MS = 1000;
const SEARCH_MIN_CHARS = 3;

const uiToParams = (ui, debouncedSearch) => {
  const params = {};
  const search = (debouncedSearch ?? ui.search).trim();
  if (search.length >= SEARCH_MIN_CHARS) params.search = search;
  if (ui.unitType) params.unit_type = ui.unitType;
  if (ui.floor !== "") params.floor = Number(ui.floor);
  if (ui.priceMin !== "") params.min_price = Number(ui.priceMin);
  if (ui.priceMax !== "") params.max_price = Number(ui.priceMax);
  // toggle off (default) → available=true (unsold); toggle on → available=false (sold)
  params.available = !ui.showSold;
  const ordering = ORDERING_OPTIONS.find((o) => o.value === ui.ordering)?.api;
  if (ordering) params.ordering = ordering;
  return params;
};

const paramsToUi = (p = {}) => {
  const ui = { ...INITIAL_UI };
  if (p.search) ui.search = String(p.search);
  if (p.unit_type) ui.unitType = String(p.unit_type);
  if (p.floor != null && p.floor !== "") ui.floor = String(p.floor);
  if (p.min_price != null && p.min_price !== "") ui.priceMin = String(p.min_price);
  if (p.max_price != null && p.max_price !== "") ui.priceMax = String(p.max_price);
  // sold view is the non-default state; everything else means "show unsold"
  ui.showSold = p.available === false || p.available === "false";
  if (p.ordering) {
    const match = ORDERING_OPTIONS.find((o) => o.api === p.ordering);
    if (match) ui.ordering = match.value;
  }
  return ui;
};

const C = {
  primary: "#6610f2",
  primarySoft: "rgba(102, 16, 242, 0.08)",
  surface: "#f7f2fa",
  surfaceRaised: "#ffffff",
  border: "rgba(73, 69, 79, 0.12)",
  text: "#1c1b1f",
  secondary: "#49454f",
  muted: "#79747e",
  track: "#dcd7df",
};

const CHEVRON_SVG =
  "url(\"data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='none' stroke='%2349454F' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpath d='m4 6 4 4 4-4'/%3e%3c/svg%3e\")";

const styles = {
  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 20,
    padding: "2rem",
    marginBottom: "2rem",
    fontFamily: "inherit",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    marginBottom: "1.5rem",
  },
  headerIcon: { fontSize: "1.125rem", color: C.primary, lineHeight: 1 },
  headerTitle: {
    margin: 0,
    fontSize: "0.9375rem",
    fontWeight: 700,
    color: C.primary,
    letterSpacing: "-0.005em",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "1.25rem 1.5rem",
    alignItems: "end",
  },
  field: { display: "flex", flexDirection: "column", minWidth: 0 },
  fieldSpan2: { gridColumn: "span 2" },
  label: {
    display: "block",
    fontSize: "0.6875rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: C.secondary,
    marginBottom: "0.5rem",
    lineHeight: 1.4,
    minHeight: "0.9625rem",
  },
  control: {
    width: "100%",
    height: 42,
    padding: "0 0.875rem",
    background: C.surfaceRaised,
    border: `1px solid ${C.border}`,
    borderRadius: 10,
    fontSize: "0.8125rem",
    fontFamily: "inherit",
    color: C.text,
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  controlFocus: {
    borderColor: C.primary,
    boxShadow: `0 0 0 3px ${C.primarySoft}`,
  },
  select: {
    paddingRight: "2.25rem",
    backgroundImage: CHEVRON_SVG,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 0.875rem center",
    backgroundSize: "1rem",
    cursor: "pointer",
  },
  searchWrap: { position: "relative" },
  searchIcon: {
    position: "absolute",
    left: "0.875rem",
    top: "50%",
    transform: "translateY(-50%)",
    color: C.muted,
    fontSize: "0.95rem",
    lineHeight: 1,
    pointerEvents: "none",
  },
  searchInput: { paddingLeft: "2.375rem" },
  priceRow: { display: "flex", alignItems: "center", gap: "0.625rem" },
  priceSep: {
    color: C.muted,
    fontSize: "0.75rem",
    fontWeight: 500,
    flexShrink: 0,
    letterSpacing: "0.06em",
  },
  toggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.75rem",
    height: 42,
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  toggleTrack: (isOn) => ({
    position: "relative",
    display: "inline-block",
    width: 44,
    height: 24,
    borderRadius: 999,
    background: isOn ? C.primary : C.track,
    transition: "background-color 0.2s ease",
    flexShrink: 0,
  }),
  toggleThumb: (isOn) => ({
    position: "absolute",
    top: 3,
    left: 3,
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#ffffff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
    transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    transform: isOn ? "translateX(20px)" : "translateX(0)",
  }),
  toggleLabel: {
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: C.secondary,
    userSelect: "none",
  },
  clearBtn: (hover) => ({
    width: "100%",
    height: 42,
    background: hover ? C.primarySoft : "transparent",
    border: `1px solid ${C.primary}`,
    color: C.primary,
    borderRadius: 10,
    fontSize: "0.8125rem",
    fontWeight: 700,
    letterSpacing: "0.01em",
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "background-color 0.15s ease",
  }),
};

const UfInput = ({ extraStyle, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      onFocus={(e) => {
        setFocused(true);
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        props.onBlur?.(e);
      }}
      style={{
        ...styles.control,
        ...(extraStyle || {}),
        ...(focused ? styles.controlFocus : {}),
      }}
    />
  );
};

const UfSelect = ({ children, ...props }) => {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      onFocus={(e) => {
        setFocused(true);
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        setFocused(false);
        props.onBlur?.(e);
      }}
      style={{
        ...styles.control,
        ...styles.select,
        ...(focused ? styles.controlFocus : {}),
      }}
    >
      {children}
    </select>
  );
};

// typeOptions: either pass explicit options, or they'll be derived empty
// initialParams: seed from URL or persisted state on first mount
const UnitFilters = ({ onFilterChange, typeOptions = [], initialParams }) => {
  const seed = useMemo(() => paramsToUi(initialParams), []);
  const [ui, setUi] = useState(seed);
  const [debouncedSearch, setDebouncedSearch] = useState(seed.search);
  const [clearHover, setClearHover] = useState(false);
  const debounceRef = useRef(null);

  // Debounce the search field specifically
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(ui.search);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [ui.search]);

  const params = useMemo(
    () => uiToParams(ui, debouncedSearch),
    [ui, debouncedSearch]
  );

  useEffect(() => {
    onFilterChange?.(params);
  }, [params, onFilterChange]);

  const update = (key) => (e) => {
    const value = e?.target?.value ?? "";
    setUi((f) => ({ ...f, [key]: value }));
  };

  const toggleBool = useCallback((key) => {
    setUi((f) => ({ ...f, [key]: !f[key] }));
  }, []);

  const clear = useCallback(() => setUi(INITIAL_UI), []);

  return (
    <div style={styles.card} role="region" aria-label="Unit filters">
      <header style={styles.header}>
        <i className="ri-filter-3-line" style={styles.headerIcon} aria-hidden="true" />
        <h4 style={styles.headerTitle}>Advanced Filters</h4>
      </header>

      <div style={styles.grid}>
        <div style={styles.field}>
          <label style={styles.label} htmlFor="uf-search">
            Search
          </label>
          <div style={styles.searchWrap}>
            <i className="ri-search-line" style={styles.searchIcon} aria-hidden="true" />
            <UfInput
              id="uf-search"
              type="text"
              placeholder="Unit # or CF..."
              value={ui.search}
              onChange={update("search")}
              extraStyle={styles.searchInput}
            />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label} htmlFor="uf-type">
            Unit Type
          </label>
          <UfSelect
            id="uf-type"
            value={ui.unitType}
            onChange={update("unitType")}
          >
            <option value="">All Types</option>
            {typeOptions.map((t) => (
              <option key={t.value || t} value={t.value || t}>
                {t.label || t}
              </option>
            ))}
          </UfSelect>
        </div>

        <div style={styles.field}>
          <label style={styles.label} htmlFor="uf-floor">
            Floor
          </label>
          <UfInput
            id="uf-floor"
            type="number"
            placeholder="Floor level"
            value={ui.floor}
            onChange={update("floor")}
          />
        </div>

        <div style={styles.field}>
          <label style={styles.label} htmlFor="uf-order">
            Ordering
          </label>
          <UfSelect id="uf-order" value={ui.ordering} onChange={update("ordering")}>
            {ORDERING_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </UfSelect>
        </div>

        <div style={{ ...styles.field, ...styles.fieldSpan2 }}>
          <label style={styles.label} htmlFor="uf-price-min">
            Price Range (Min – Max)
          </label>
          <div style={styles.priceRow}>
            <UfInput
              id="uf-price-min"
              type="number"
              placeholder="Min Price"
              value={ui.priceMin}
              onChange={update("priceMin")}
            />
            <span style={styles.priceSep}>to</span>
            <UfInput
              id="uf-price-max"
              type="number"
              placeholder="Max Price"
              value={ui.priceMax}
              onChange={update("priceMax")}
            />
          </div>
        </div>

        <div style={styles.field}>
          <span style={styles.label}>Availability</span>
          <button
            type="button"
            style={styles.toggle}
            role="switch"
            aria-checked={ui.showSold}
            onClick={() => toggleBool("showSold")}
          >
            <span style={styles.toggleTrack(ui.showSold)}>
              <span style={styles.toggleThumb(ui.showSold)} />
            </span>
            <span style={styles.toggleLabel}>
              {ui.showSold ? "Showing sold units" : "Showing unsold units"}
            </span>
          </button>
        </div>

        <div style={styles.field}>
          <span style={{ ...styles.label, visibility: "hidden" }}>.</span>
          <button
            type="button"
            style={styles.clearBtn(clearHover)}
            onClick={clear}
            onMouseEnter={() => setClearHover(true)}
            onMouseLeave={() => setClearHover(false)}
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnitFilters;
